/**
 * @file Modal for updating an account's phone number.
 */

import React from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';

import AccountUpdatePhoneNumberForm from '../../components/auth/AccountUpdatePhoneNumberForm';
import withModal from '../../components/global/modal/withModal';
import { MeQuery } from '../../graphql/queries/account';
import { ACCOUNT_UPDATE_PHONE_NUMBER_MODAL } from '../../constants/modals';
import {
  phoneVerificationRequest as phoneVerificationRequestMutation,
  accountUpdatePhoneNumber as accountUpdatePhoneNumberMutation,
} from '../../graphql/mutations/account';
import WaldoSpeechBubble from '../../components/global/WaldoSpeechBubble';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  onRequestClose: PropTypes.func.isRequired,
  accountUpdatePhoneNumber: PropTypes.func.isRequired,
  phoneVerificationRequest: PropTypes.func.isRequired,
  actionProps: PropTypes.shape({
    onAccountUpdated: PropTypes.func,
  }),
};

const AccountUpdatePhoneNumberContainer = ({
  account,
  onRequestClose,
  actionProps = { onAccountUpdated: () => {} },
  accountUpdatePhoneNumber,
  phoneVerificationRequest,
}) => {
  return account ? (
    <AccountUpdatePhoneNumberForm
      introContent={
        <WaldoSpeechBubble position="right" className="mx-0 mb-1 w-100-i">
          Add your mobile number and I&apos;ll text you when I find your photos!
        </WaldoSpeechBubble>
      }
      verificationContent={formattedPhone => (
        <WaldoSpeechBubble position="right" className="mx-0 mb-1 w-100-i">
          Please enter the code sent to{' '}
          <span className="text-primary text-nowrap">{formattedPhone}</span>
        </WaldoSpeechBubble>
      )}
      account={account}
      accountUpdatePhoneNumber={accountUpdatePhoneNumber}
      phoneVerificationRequest={phoneVerificationRequest}
      onAccountUpdated={() => {
        onRequestClose();
        actionProps.onAccountUpdated();
      }}
    />
  ) : null;
};

AccountUpdatePhoneNumberContainer.propTypes = propTypes;

const withUser = graphql(MeQuery, {
  props: ({ data }) => ({
    account: data.me,
  }),
});

const withPhoneVerificationRequestMutation = graphql(
  phoneVerificationRequestMutation,
  {
    props: ({ mutate }) => ({
      phoneVerificationRequest: (phoneNumber, intent) =>
        mutate({
          variables: {
            input: {
              phone_number: phoneNumber,
              intent,
            },
          },
        }),
    }),
  },
);

const withAccountUpdatePhoneNumberMutation = graphql(
  accountUpdatePhoneNumberMutation,
  {
    props: ({ mutate }) => ({
      accountUpdatePhoneNumber: (accountId, phoneNumber, code) =>
        mutate({
          variables: {
            input: {
              account_id: accountId,
              phone_number: phoneNumber,
              phone_verification_code: code,
            },
          },
        }),
    }),
  },
);

export default withModal(
  compose(
    withUser,
    withPhoneVerificationRequestMutation,
    withAccountUpdatePhoneNumberMutation,
  )(AccountUpdatePhoneNumberContainer),
  {
    name: ACCOUNT_UPDATE_PHONE_NUMBER_MODAL,
    size: 'sm',
    contentLabel: 'Add your mobile number',
  },
);
